import { useEffect } from 'react';

import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import QuestionIcon from '@/components/icons/question';
import { NotificationsTabSkeleton } from '@/components/settings/settings-form-skeleton';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { getWorkOrderNotificationsQueryOptions, workOrderNotificationKeys } from '@/queries/notifications';
import { ApiService } from '@/services/api-v2';

const workOrderSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
});

type WorkOrderNotificationsFormValues = z.infer<typeof workOrderSchema>;

export function NotificationsWorkOrder() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();

  const form = useForm<WorkOrderNotificationsFormValues>({
    resolver: zodResolver(workOrderSchema),
    defaultValues: { email: false, sms: false },
    mode: 'onChange',
  });

  const { control, handleSubmit, formState, reset } = form;

  const { data: workOrderData, isLoading: loading, isError } = useQuery(getWorkOrderNotificationsQueryOptions());

  useEffect(() => {
    if (workOrderData) {
      reset({ email: workOrderData.email, sms: workOrderData.sms });
    }
  }, [workOrderData, reset]);

  const { mutateAsync: mutateWorkOrderAsync, isPending: isPendingWorkOrder } = useMutation({
    mutationFn: async (values: WorkOrderNotificationsFormValues) => await (await ApiService.UpdateNotifyWorkOrder(values)).json(),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutateWorkOrderAsync(data);
      reset({ email: result.email, sms: result.sms });
      queryClient.setQueryData(workOrderNotificationKeys.all, result);
      toast.success('Notification preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update work order notifications');
      console.error('Error updating work order settings:', error);
    }
  });

  if (loading) {
    return <NotificationsTabSkeleton />;
  }

  if (isError) {
    return <p className="text-secondary-text text-sm">Unable to load work order notification settings.</p>;
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium">Work Order Notifications</h2>
        <p className="text-default-text text-sm">
          The settings below allow you to control email and SMS notifications when work orders are assigned to a user.
        </p>
      </div>

      <div className="flex w-full items-center gap-4">
        {/* 300px label column is this screen's field-row width from the design; the width is set per screen, not shared */}
        <Label htmlFor="notify-work-order-email" className="text-default-text w-[300px] shrink-0 text-sm font-normal">
          Send email to assignee
        </Label>
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name="email"
            render={({ field: { value, onChange } }) => (
              <Switch id="notify-work-order-email" checked={value} onCheckedChange={onChange} disabled={isPendingWorkOrder || !canEditWorkspaceSettings} />
            )}
          />
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="More information">
                  <QuestionIcon className="text-default-text h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent className="max-w-[290px]">
                  <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                  Gearbelt Contributors will receive an email when a work order is assigned to them or updated
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex w-full items-center gap-4">
        <Label htmlFor="notify-work-order-sms" className="text-default-text w-[300px] shrink-0 text-sm font-normal">
          Send SMS to assignee
        </Label>
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name="sms"
            render={({ field: { value, onChange } }) => (
              <Switch id="notify-work-order-sms" checked={value} onCheckedChange={onChange} disabled={isPendingWorkOrder || !canEditWorkspaceSettings} />
            )}
          />
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="More information">
                  <QuestionIcon className="text-default-text h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent className="max-w-[290px]">
                  <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                  <p>Gearbelt Contributors will receive an SMS when a work order is assigned to them or updated.</p>
                  <p>Those without a mobile number will receive an email instead, even if email is off.</p>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {canEditWorkspaceSettings && (
        <Button
          type="submit"
          variant="success"
          className="w-fit"
          disabled={isPendingWorkOrder || !formState.isValid || formState.isSubmitting || !formState.isDirty}
          loading={isPendingWorkOrder}>
          Save changes
        </Button>
      )}
    </form>
  );
}
